import { Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { CardContent } from '@/shared/components/card-content/card-content';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { PrestadorRecursosMateriaisService } from '@/core/api/sicom-api/registro/prestador/prestador-recursos-materiais.service';
import { PrestadorRecursoMaterial, PrestadorRecursoMaterialRequest } from '@/core/api/sicom-api/registro/prestador/prestador-recursos-materiais.model';
import { DateUtils } from '@/shared/utils/date-utils';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-recursos-materiais-prestador',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, ReactiveFormsModule, BotaoAcoes, Button, CardContent, ConfirmDialog, CustomTableComponent, Dialog, InputNumber, Select],
    providers: [ConfirmationService],
    templateUrl: './recursos-materiais-prestador.html',
    styleUrls: ['./recursos-materiais-prestador.scss']
})
export class RecursosMaterialsPrestador {
    pessoa = input<Pessoa>();

    private readonly fb = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    private readonly service = inject(PrestadorRecursosMateriaisService);
    private readonly confirmation = inject(ConfirmationService);
    private readonly notificacoes = inject(NotificacoesService);
    readonly linhas = signal<PrestadorRecursoMaterial[]>([]);
    readonly linhasTabela = computed(() => this.linhas().map((item) => ({ ...item, dtInclusao: DateUtils.formatDate(item.dtInclusao) })));
    readonly tipos = signal<PrestadorRecursoMaterial[]>([]);
    readonly carregando = signal(false);
    readonly salvando = signal(false);
    editando = false;
    itemEditado?: PrestadorRecursoMaterial;
    readonly colunaOrdem = ['tipo', 'recursoMaterial', 'quantidade', 'dtInclusao'];
    protected readonly roleEditar = PERMISSOES.prestadores.recursosMateriais.editar;
    protected readonly EInconPos = EInconPos;

    get itensAcoes(): AppMenuItem[] {
        return [
            acaoMenuItem({ id: 'novo', label: 'Novo', icon: 'pi pi-plus', cor: 'novo', permission: this.roleEditar, command: () => this.novo() }),
            acaoMenuItem({ id: 'atualizar', label: 'Atualizar', icon: 'pi pi-refresh', cor: 'info', disabled: this.carregando(), command: () => this.carregar() })
        ];
    }

    readonly metadata: TableMetadata = {
        headers: {
            tipo: { type: 'text', value: 'Tipo', default: '-', hidden: false },
            recursoMaterial: { type: 'text', value: 'Recurso Material', default: '-', hidden: false },
            quantidade: { type: 'text', value: 'Quantidade', default: '-', hidden: false },
            dtInclusao: { type: 'text', value: 'Inclusão', default: '-', hidden: true }
        },
        title: 'recursoMaterial',
        actions: {
            editar: { label: 'Editar', icon: 'pi pi-pencil', color: 'info', command: (item: PrestadorRecursoMaterial) => this.editar(item), permission: this.roleEditar },
            remover: { label: 'Remover', icon: 'pi pi-trash', color: 'danger', command: (item: PrestadorRecursoMaterial) => this.confirmarRemocao(item), permission: this.roleEditar }
        },
        defaultSortField: 'recursoMaterial',
        defaultSortOrder: 1
    };
    readonly form = this.fb.group({ idRecursoMaterial: this.fb.control<number | null>(null, Validators.required), quantidade: this.fb.control<number | null>(1, [Validators.required, Validators.min(1)]) });
    constructor() {
        effect(() => {
            const p = this.pessoa();
            if (p?.id && p.uf) {
                this.carregar();
                this.service
                    .tipos()
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((x) => this.tipos.set(x));
            }
        });
    }
    carregar() {
        const p = this.pessoa();
        if (!p) return;
        this.carregando.set(true);
        this.service
            .listar(p.id, p.uf)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe({
                next: (x) => this.linhas.set(x),
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => this.linhas.set([])
            });
    }
    novo() {
        this.itemEditado = undefined;
        this.form.reset({ quantidade: 1 });
        this.editando = true;
    }
    editar(item: PrestadorRecursoMaterial) {
        this.itemEditado = item;
        this.form.reset({ idRecursoMaterial: item.idRecursoMaterial, quantidade: item.quantidade });
        this.editando = true;
    }
    salvar() {
        const p = this.pessoa();
        if (!p || this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const v = this.form.getRawValue();
        const r: PrestadorRecursoMaterialRequest = { id: p.id, uf: p.uf, idRecursoMaterial: v.idRecursoMaterial!, quantidade: v.quantidade!, dtInclusao: this.itemEditado?.dtInclusao, dtInclusaoOriginal: this.itemEditado?.dtInclusao };
        this.salvando.set(true);
        (this.itemEditado ? this.service.atualizar(r) : this.service.criar(r))
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.salvando.set(false))
            )
            // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
            .subscribe(() => {
                this.editando = false;
                this.carregar();
                this.notificacoes.notificar({ severity: 'success', summary: 'Recurso material salvo.' });
            });
    }
    confirmarRemocao(item: PrestadorRecursoMaterial) {
        const p = this.pessoa();
        if (!p) return;
        this.confirmation.confirm({
            message: `Remover ${item.recursoMaterial}?`,
            accept: () =>
                this.service
                    .remover({ id: p.id, uf: p.uf, idRecursoMaterial: item.idRecursoMaterial, quantidade: item.quantidade!, dtInclusao: item.dtInclusao })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    // Recusa nao desfaz nada aqui de proposito: o formulario/dialogo fica como esta para o usuario corrigir, e o toast vem do interceptor global.
                    .subscribe(() => {
                        this.carregar();
                        this.notificacoes.notificar({ severity: 'success', summary: 'Recurso material removido.' });
                    })
        });
    }
    get total() {
        return this.linhas().reduce((v, i) => v + (i.quantidade ?? 0), 0);
    }
}
